import React from 'react';

export const StatCardSkeleton: React.FC = () => (
    <div
        className="p-4 rounded-lg shadow-md animate-pulse border border-theme-divider"
        style={{ backgroundColor: 'rgb(var(--color-stats-container-bg-rgb) / var(--color-stats-container-bg-opacity))' }}
    >
        <div className="flex justify-between items-start">
            <div className="w-full">
                <div className="h-4 bg-theme-bg-surface/50 dark:bg-dark-bgAccent rounded w-1/2 mb-3"></div>
                <div className="h-8 bg-theme-bg-surface/80 dark:bg-dark-bgAccent/80 rounded w-1/3 mb-4"></div>
                <div className="h-3 bg-theme-bg-surface/50 dark:bg-dark-bgAccent rounded w-2/3"></div>
            </div>
            <div className="h-12 w-12 bg-theme-bg-surface/50 dark:bg-dark-bgAccent rounded-full"></div>
        </div>
    </div>
);

export const ChartWidgetSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div
        className={`p-6 rounded-lg shadow-md animate-pulse flex flex-col border border-theme-divider ${className}`}
        style={{ backgroundColor: 'rgb(var(--color-stats-container-bg-rgb) / var(--color-stats-container-bg-opacity))' }}
    >
        <div className="h-6 bg-theme-bg-surface/50 dark:bg-dark-bgAccent rounded w-1/3 mb-6"></div>
        <div className="flex-1 flex justify-center items-center">
            <div className="h-48 w-48 rounded-full bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 relative">
                <div className="absolute inset-4 rounded-full bg-theme-bg-secondary dark:bg-dark-bgSecondary"></div>
            </div>
        </div>
    </div>
);

export const SystemHealthSkeleton: React.FC = () => (
    <div
        className="p-6 rounded-lg shadow-md flex flex-col h-full border border-theme-divider animate-pulse"
        style={{ backgroundColor: 'rgb(var(--color-stats-container-bg-rgb) / var(--color-stats-container-bg-opacity))' }}
    >
        <div className="flex justify-between items-center mb-6">
            <div className="h-6 bg-theme-bg-surface/50 dark:bg-dark-bgAccent rounded w-1/3"></div>
            <div className="h-6 bg-theme-bg-surface/50 dark:bg-dark-bgAccent rounded-full w-16"></div>
        </div>

        <div className="flex-1 flex flex-col lg:flex-row gap-10 mb-8 items-stretch">
            <div className="flex-1 flex justify-center items-center py-4">
                <div className="w-48 h-24 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded-t-full"></div>
            </div>
            <div className="flex-1 h-32 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded-lg lg:border-l border-theme-divider/10 lg:pl-10"></div>
        </div>

        <div className="mt-auto space-y-2 overflow-y-auto max-h-72">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="h-8 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded w-full"></div>
            ))}
        </div>
    </div>
);

export const AdminDashboardSkeleton: React.FC = () => (
    <div className="space-y-6">
        {/* KPI Cards Layer */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
        </div>

        {/* Top Priority Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="h-full">
                <ChartWidgetSkeleton className="h-full min-h-[380px]" />
            </div>
            <div className="lg:col-span-2">
                <SystemHealthSkeleton />
            </div>
        </div>

        {/* Secondary Insights Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-6 rounded-lg shadow-md animate-pulse border border-theme-divider min-h-[300px]" style={{ backgroundColor: 'rgb(var(--color-stats-container-bg-rgb) / var(--color-stats-container-bg-opacity))' }}>
                <div className="h-6 bg-theme-bg-surface/50 dark:bg-dark-bgAccent rounded w-1/4 mb-4"></div>
                <div className="space-y-3">
                    <div className="h-10 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded w-full"></div>
                    <div className="h-10 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded w-full"></div>
                    <div className="h-10 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded w-full"></div>
                </div>
            </div>
            <ChartWidgetSkeleton className="min-h-[300px]" />
        </div>

        {/* Oversight & Accountability Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-6">
            <div className="lg:col-span-2">
                <ChartWidgetSkeleton className="min-h-[320px]" />
            </div>
            <div className="p-6 rounded-lg shadow-md animate-pulse border border-theme-divider h-full min-h-[320px]" style={{ backgroundColor: 'rgb(var(--color-stats-container-bg-rgb) / var(--color-stats-container-bg-opacity))' }}>
                <div className="h-6 bg-theme-bg-surface/50 dark:bg-dark-bgAccent rounded w-1/3 mb-6"></div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="h-12 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded-lg"></div>
                    <div className="h-12 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded-lg"></div>
                    <div className="h-12 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded-lg"></div>
                    <div className="h-12 bg-theme-bg-surface/30 dark:bg-dark-bgAccent/30 rounded-lg"></div>
                </div>
            </div>
        </div>
    </div>
);

